import React from 'react';
import styled from 'styled-components';
import { Button } from '../../design-system/components/Button';
import { Heading, Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { borders } from '../../design-system/tokens/borders';
import { typography } from '../../design-system/tokens/typography';
import { hexToRgba } from '../../utils/colorUtils';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

const Strip = styled.aside`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[4]};
  padding: ${spacing[5]} ${spacing[6]};
  border-radius: ${borders.radius.lg};
  border: 1px solid ${props => hexToRgba(props.theme.colors.primary, 0.28)};
  background: ${props => hexToRgba(props.theme.colors.primary, 0.06)};
  box-shadow: 0 8px 24px ${props => hexToRgba(props.theme.colors.shadow || '#000', 0.06)};
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  min-width: 0;
  flex: 1;
`;

const Title = styled(Heading)`
  margin: 0 !important;
  font-size: ${typography.fontSize.cardHeading};
`;

const Message = styled(Text)`
  display: block;
`;

const Cta = styled(Button)`
  flex-shrink: 0;
`;

const Arrow = styled(ArrowRightIcon)`
  width: 18px;
  height: 18px;
`;

export interface PrimaryNextActionProps {
  title: string;
  message: string;
  actionLabel: string;
  onAction: () => void;
}

/**
 * Single elevated “what should I do next?” strip for dashboards.
 */
export const PrimaryNextAction: React.FC<PrimaryNextActionProps> = ({
  title,
  message,
  actionLabel,
  onAction,
}) => (
  <Strip aria-label={title}>
    <Copy>
      <Title variant="h3" as="h2">
        {title}
      </Title>
      <Message size="sm" color="secondary">
        {message}
      </Message>
    </Copy>
    <Cta type="button" variant="primary" size="md" onClick={onAction}>
      {actionLabel}
      <Arrow aria-hidden style={{ marginLeft: 8 }} />
    </Cta>
  </Strip>
);

export default PrimaryNextAction;
